import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import Avatar from "@material-ui/core/Avatar";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import AssignmentIcon from "@material-ui/icons/Assignment";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import RepeatIcon from "@material-ui/icons/Repeat";
import NotificationsIcon from "@material-ui/icons/Notifications";
import PeopleIcon from "@material-ui/icons/People";

import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    padding: "12px 20px",
  },
  titleLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  content: {
    padding: theme.spacing(3),
  },
  priorityContainer: {
    display: "flex",
    gap: 8,
    marginTop: 8,
  },
  priorityChip: {
    cursor: "pointer",
    fontWeight: 600,
    transition: "all 0.2s",
    "&:hover": {
      transform: "scale(1.05)",
    },
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontWeight: 600,
    fontSize: "0.9rem",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    color: theme.mode === "light" ? "#555" : "#ccc",
  },
}));

const initialState = {
  title: "",
  description: "",
  priority: "medium",
  dueDate: "",
  startTime: "",
  endTime: "",
  recurrence: "none",
  reminderMinutes: "",
  assignedToUserIds: [],
  showSchedule: false,
};

const TaskModal = ({ open, onClose, task, users = [] }) => {
  const classes = useStyles();
  const [formData, setFormData] = useState(initialState);

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || "",
        description: task.description || "",
        priority: task.priority || "medium",
        dueDate: task.dueDate || "",
        startTime: task.startTime || "",
        endTime: task.endTime || "",
        recurrence: task.recurrence || "none",
        reminderMinutes: task.reminderMinutes || "",
        assignedToUserIds: task.assignedToUserIds || [],
        showSchedule: !!(task.startTime || task.endTime),
      });
    } else {
      setFormData(initialState);
    }
  }, [task, open]);

  const handleChange = (field) => (e, child) => {
    const value = e && e.target ? e.target.value : (child ? child.props.value : "");
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      // Ao selecionar lembrete, ativar horário automaticamente
      if (field === "reminderMinutes" && value) {
        updated.showSchedule = true;
      }
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error(i18n.t("tasks.validation.titleRequired"));
      return;
    }

    if (formData.reminderMinutes && !formData.startTime) {
      toast.error(i18n.t("tasks.validation.reminderNeedsTime"));
      return;
    }

    const payload = {
      title: formData.title,
      description: formData.description,
      priority: formData.priority,
      dueDate: formData.dueDate || null,
      startTime: formData.showSchedule ? formData.startTime || null : null,
      endTime: formData.showSchedule ? formData.endTime || null : null,
      recurrence: formData.recurrence,
      reminderMinutes: formData.reminderMinutes ? Number(formData.reminderMinutes) : null,
      assignedToUserIds: formData.assignedToUserIds.length > 0 ? formData.assignedToUserIds : null,
    };

    try {
      if (task) {
        await api.put(`/tasks/${task.id}`, payload);
        toast.success(i18n.t("tasks.toasts.updated"));
      } else {
        await api.post("/tasks", payload);
        toast.success(i18n.t("tasks.toasts.created"));
      }
      onClose();
    } catch (err) {
      toastError(err);
    }
  };

  const priorityOptions = [
    { value: "low", label: i18n.t("tasks.priority.low"), color: "#22c55e" },
    { value: "medium", label: i18n.t("tasks.priority.medium"), color: "#f59e0b" },
    { value: "high", label: i18n.t("tasks.priority.high"), color: "#ef4444" },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <div className={classes.dialogTitle}>
        <div className={classes.titleLeft}>
          <AssignmentIcon />
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            {task ? i18n.t("tasks.modal.editTitle") : i18n.t("tasks.modal.newTitle")}
          </Typography>
        </div>
        <IconButton size="small" onClick={onClose} style={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent className={classes.content}>
        <TextField
          label={i18n.t("tasks.modal.titleField")}
          variant="outlined"
          fullWidth
          required
          value={formData.title}
          onChange={handleChange("title")}
          margin="dense"
          autoFocus
        />

        <TextField
          label={i18n.t("tasks.modal.description")}
          variant="outlined"
          fullWidth
          multiline
          rows={3}
          value={formData.description}
          onChange={handleChange("description")}
          margin="dense"
        />

        {/* Priority */}
        <Typography className={classes.sectionTitle}>
          <ErrorIcon style={{ fontSize: "1.1rem" }} />
          {i18n.t("tasks.modal.priority")}
        </Typography>
        <div className={classes.priorityContainer}>
          {priorityOptions.map((opt) => (
            <Chip
              key={opt.value}
              label={opt.label}
              className={classes.priorityChip}
              onClick={() => setFormData((prev) => ({ ...prev, priority: opt.value }))}
              style={{
                backgroundColor: formData.priority === opt.value ? opt.color : `${opt.color}20`,
                color: formData.priority === opt.value ? "#fff" : opt.color,
                border: `1px solid ${opt.color}`,
              }}
            />
          ))}
        </div>

        {/* Due Date & Assigned To */}
        <Grid container spacing={2} style={{ marginTop: 8 }}>
          <Grid item xs={6}>
            <TextField
              label={i18n.t("tasks.modal.dueDate")}
              type="date"
              variant="outlined"
              fullWidth
              value={formData.dueDate}
              onChange={handleChange("dueDate")}
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl variant="outlined" fullWidth margin="dense">
              <InputLabel>{i18n.t("tasks.modal.assignTo")}</InputLabel>
              <Select
                multiple
                value={formData.assignedToUserIds}
                onChange={(e) => setFormData((prev) => ({ ...prev, assignedToUserIds: e.target.value }))}
                label={i18n.t("tasks.modal.assignTo")}
                renderValue={(selected) => (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {selected.length === 0 ? (
                      <Chip
                        size="small"
                        icon={<PeopleIcon style={{ fontSize: 16 }} />}
                        label={i18n.t("tasks.filters.all")}
                        variant="outlined"
                      />
                    ) : (
                      selected.map((id) => {
                        const u = users.find((u) => u.id === id);
                        return (
                          <Chip
                            key={id}
                            size="small"
                            avatar={<Avatar style={{ width: 22, height: 22, fontSize: 12 }}>{(u?.name || "?")[0]}</Avatar>}
                            label={u?.name || id}
                            onDelete={() => setFormData((prev) => ({
                              ...prev,
                              assignedToUserIds: prev.assignedToUserIds.filter((i) => i !== id)
                            }))}
                            onMouseDown={(e) => e.stopPropagation()}
                          />
                        );
                      })
                    )}
                  </div>
                )}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 300 } },
                  anchorOrigin: { vertical: "bottom", horizontal: "left" },
                  getContentAnchorEl: null,
                }}
              >
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id} dense>
                    <Checkbox
                      checked={formData.assignedToUserIds.includes(u.id)}
                      color="primary"
                      size="small"
                    />
                    <Avatar style={{ width: 24, height: 24, fontSize: 12, marginRight: 8 }}>
                      {u.name[0]}
                    </Avatar>
                    <ListItemText primary={u.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Schedule Toggle */}
        <Typography className={classes.sectionTitle}>
          <AccessTimeIcon style={{ fontSize: "1.1rem" }} />
          {i18n.t("tasks.modal.schedule")}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={formData.showSchedule}
              onChange={(e) => setFormData((prev) => ({ ...prev, showSchedule: e.target.checked }))}
              color="primary"
            />
          }
          label=""
        />

        {formData.showSchedule && (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label={i18n.t("tasks.modal.startTime")}
                type="time"
                variant="outlined"
                fullWidth
                value={formData.startTime}
                onChange={handleChange("startTime")}
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={i18n.t("tasks.modal.endTime")}
                type="time"
                variant="outlined"
                fullWidth
                value={formData.endTime}
                onChange={handleChange("endTime")}
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        )}

        {/* Reminder - só mostra quando tem horário */}
        {formData.showSchedule && formData.startTime && (
        <>
        <Typography className={classes.sectionTitle}>
          <NotificationsIcon style={{ fontSize: "1.1rem" }} />
          {i18n.t("tasks.modal.reminder")}
        </Typography>
        <FormControl variant="outlined" fullWidth margin="dense">
          <InputLabel>{i18n.t("tasks.modal.notifyBefore")}</InputLabel>
          <Select
            value={formData.reminderMinutes}
            onChange={handleChange("reminderMinutes")}
            label={i18n.t("tasks.modal.notifyBefore")}
          >
            <MenuItem value="">{i18n.t("tasks.reminder.none")}</MenuItem>
            <MenuItem value={5}>5 {i18n.t("tasks.reminder.minutes")}</MenuItem>
            <MenuItem value={10}>10 {i18n.t("tasks.reminder.minutes")}</MenuItem>
            <MenuItem value={15}>15 {i18n.t("tasks.reminder.minutes")}</MenuItem>
            <MenuItem value={30}>30 {i18n.t("tasks.reminder.minutes")}</MenuItem>
            <MenuItem value={60}>1 {i18n.t("tasks.reminder.hour")}</MenuItem>
            <MenuItem value={120}>2 {i18n.t("tasks.reminder.hours")}</MenuItem>
            <MenuItem value={1440}>1 {i18n.t("tasks.reminder.day")}</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
          {i18n.t("tasks.modal.reminderHelp")}
        </Typography>
        </>
        )}

        {/* Recurrence */}
        <Typography className={classes.sectionTitle}>
          <RepeatIcon style={{ fontSize: "1.1rem" }} />
          {i18n.t("tasks.modal.recurrence")}
        </Typography>
        <FormControl variant="outlined" fullWidth margin="dense">
          <InputLabel>{i18n.t("tasks.modal.recurrence")}</InputLabel>
          <Select
            value={formData.recurrence}
            onChange={handleChange("recurrence")}
            label={i18n.t("tasks.modal.recurrence")}
          >
            <MenuItem value="none">{i18n.t("tasks.recurrence.none")}</MenuItem>
            <MenuItem value="daily">{i18n.t("tasks.recurrence.daily")}</MenuItem>
            <MenuItem value="weekly">{i18n.t("tasks.recurrence.weekly")}</MenuItem>
            <MenuItem value="monthly">{i18n.t("tasks.recurrence.monthly")}</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions style={{ padding: "12px 20px" }}>
        <Button onClick={onClose} color="default" variant="outlined">
          {i18n.t("tasks.buttons.cancel")}
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">
          {task ? i18n.t("tasks.buttons.save") : i18n.t("tasks.buttons.create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ErrorIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
  </svg>
);

export default TaskModal;
